import Highlighter from "react-highlight-words";
import { useIntl } from "react-intl";
import { Link } from "react-router-dom";
import { CommandItem } from "cmdk";
import { format, formatDuration, intervalToDuration } from "date-fns";

import { Badge } from "@/components/ui/Badge";
import { ArrowRight16 } from "@/components/ui/SvgIcon";

import { Alert } from "@/features/alerts/types";

import { dateFormat } from "@/constants/dateFormat";
import { ROUTES } from "@/constants/routes";

import { useAlertStatusTranslations } from "@/hooks/translations/useAlertStatusTranslations";

type Props = {
  alert: Alert;
  onSelect?: (to: string) => void;
  query?: string;
};

export const SearchItemAlert = ({
  alert,
  onSelect = () => {},
  query = "",
}: Props) => {
  const intl = useIntl();
  const messages = useAlertStatusTranslations();

  const to = `${ROUTES.ALERTS}/${alert.chorusAlertId}`;

  const getDuration = (start: Date, end: Date) => {
    const [amount, unit] = formatDuration(
      intervalToDuration({
        start,
        end,
      }),
      {
        format: ["days", "hours", "minutes"],
      },
    )
      .split(" ")
      .splice(0, 2);

    const shortUnit = unit[0].toLowerCase();
    return `${amount}${shortUnit}`;
  };

  const getIntervalTime = (start: Date, end: Date) => {
    const intervalTime = end.getTime() - start.getTime();
    if (intervalTime < 60 * 1000) return null;

    return intervalTime;
  };

  const getIssueTimeElapsed = (
    incidentTime: string,
    resolutionTime?: string,
  ) => {
    const incidentDate = new Date(incidentTime);
    const resolutionDate = resolutionTime
      ? new Date(resolutionTime)
      : new Date();

    const intervalTime = getIntervalTime(incidentDate, resolutionDate);
    if (!intervalTime) return null;

    return (
      <Badge className="font-manrope" variant="gray">
        {getDuration(incidentDate, resolutionDate)}
      </Badge>
    );
  };

  const getTimeStartToEnd = (incidentTime: string, resolutionTime?: string) => {
    const incidentDate = new Date(incidentTime);
    const formattedIncidentTime = format(
      incidentDate,
      dateFormat.popupTimestamp,
    );

    if (!resolutionTime)
      return <div className="truncate">{formattedIncidentTime}</div>;

    const resolutionDate = new Date(resolutionTime);
    const intervalTime = getIntervalTime(incidentDate, resolutionDate);

    if (!intervalTime)
      return <div className="truncate">{formattedIncidentTime}</div>;

    const isResolvedSameDay =
      incidentDate.toISOString().slice(0, 10) ===
      resolutionDate.toISOString().slice(0, 10);

    let formattedResolutionTime;
    if (isResolvedSameDay)
      formattedResolutionTime = format(
        resolutionDate,
        dateFormat.hoursMinutes12WithSpace,
      );
    else
      formattedResolutionTime = format(
        resolutionDate,
        dateFormat.popupTimestamp,
      );

    return (
      <>
        <div className="truncate">{formattedIncidentTime}</div>
        <ArrowRight16 data-testid="arrow-right-icon" />
        <div className="truncate">{formattedResolutionTime}</div>
      </>
    );
  };

  return (
    <CommandItem
      data-testid="search-item"
      onSelect={() => onSelect(to)}
      className="group relative cursor-pointer select-none items-center bg-white px-3 py-[2px] text-sm outline-none first:before:block first:before:h-[12px] first:before:w-full first:before:content-[''] last:after:block last:after:h-[12px] last:after:w-full last:after:content-['']   data-[disabled='true']:pointer-events-none data-[disabled='true']:opacity-50 [&:not(:last-child)]:mb-2"
    >
      <Link
        to={to}
        className="flex h-full min-h-[56px] items-start rounded-md py-[7px] pl-3 pr-2 group-aria-selected:bg-[#F8FAFC]"
      >
        <div className="flex flex-1 flex-col gap-1 overflow-hidden">
          <div className="grid grid-cols-[1fr_auto] items-center justify-between gap-1 font-sora text-sm font-medium tracking-[-0.003em] text-chorus-black-200">
            <Highlighter
              className="truncate"
              highlightClassName=" bg-chorus-teal-100 rounded-[2px] text-chorus-black-100"
              searchWords={query?.split(" ")}
              autoEscape={true}
              textToHighlight={alert.alertConfig.name}
            />
            <div className="flex gap-[6px]">
              {alert.resolutionTime ? (
                <Badge className="font-manrope" variant="green">
                  {intl.formatMessage(messages.resolved)}
                </Badge>
              ) : (
                <Badge className="font-manrope" variant="red">
                  {intl.formatMessage(messages.ongoing)}
                </Badge>
              )}
              {getIssueTimeElapsed(alert.incidentTime, alert.resolutionTime)}
            </div>
          </div>

          <div className="flex items-center gap-1 text-xs font-medium text-chorus-gray-800">
            {getTimeStartToEnd(alert.incidentTime, alert.resolutionTime)}
          </div>
        </div>
      </Link>
    </CommandItem>
  );
};
